import React, { useEffect, useMemo, useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import { Field, Form as FinalForm } from 'react-final-form';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { Form, Button, FieldTextInput, IconCollection, IconProfileCard } from '../../components';

import css from './UploadDocumentForm.module.css';
import { GENERAL_GPT, GENERAL_GPT_EDIT } from '../../util/types';
const ACCEPT_FILES = '.pdf,';

const RULEBOOK_INDEXING_STATUS = {
  DONE: 'done',
  INDEXING: 'indexing',
  FAILED: 'failed',
};

const getRulebookStatusClass = status => {
  if (status === RULEBOOK_INDEXING_STATUS.FAILED) {
    return css.statusFailed;
  }
  if (status === RULEBOOK_INDEXING_STATUS.INDEXING || status === 'pending') {
    return css.statusIndexing;
  }
  return css.statusDone;
};

const getRulebookDisplayStatus = status => {
  if (status === 'pending') {
    return RULEBOOK_INDEXING_STATUS.INDEXING;
  }
  return status || RULEBOOK_INDEXING_STATUS.DONE;
};

const isRulebookProcessing = status => status === RULEBOOK_INDEXING_STATUS.INDEXING || status === 'pending';

const idsMatch = (left, right) =>
  left == null || right == null ? false : String(left) === String(right);

const getStatusForRulebookFile = ({ rulebook, statusResponse }) => {
  if (!statusResponse || !rulebook) {
    return null;
  }

  const fileStatus = Array.isArray(statusResponse.files)
    ? statusResponse.files.find(file => idsMatch(file.file_id, rulebook.file_id))
    : null;

  if (fileStatus) {
    return fileStatus;
  }

  const rulebookMatches = statusResponse.rulebook_id
    ? idsMatch(statusResponse.rulebook_id, rulebook.rulebook_id)
    : true;
  const groupMatches = statusResponse.group_id
    ? idsMatch(statusResponse.group_id, rulebook.group_id)
    : true;

  return rulebookMatches && groupMatches ? statusResponse : null;
};

const RulebookProgress = ({ status, uploadInProgress, progressPercent }) => {
  if (!uploadInProgress && !isRulebookProcessing(status)) {
    return null;
  }
  const percent = Math.min(Math.max(progressPercent || (uploadInProgress ? 8 : 95), 0), 100);

  return (
    <div className={css.progressWrapper}>
      <div className={css.progressTrack}>
        <div className={css.progressBar} style={{ width: `${percent}%` }} />
      </div>
      <span className={css.progressText}>
        {uploadInProgress ? 'Uploading rulebook' : 'Indexing rulebook'} {percent}%
      </span>
    </div>
  );
};

const UploadDocumentFormComponent = props => {
  if (typeof window == 'undefined') return;
  const heic2any = require('heic2any');

  const [PdfPreview, setPdfPreview] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLogoRemoved, setIsLogoRemoved] = useState(false);
  const [failedPdfPreviews, setFailedPdfPreviews] = useState({});
  const [uploadProgressPercent, setUploadProgressPercent] = useState(0);
  const {
    className,
    handleSubmit,
    uploadInProgress,
    uploadError,
    form,
    values,
    specificRulebookData = [],
    genralRulebook,
    handleDeleteRulebook,
    generalRulebookData = [],
    isPrimaryGptEditOpen,
    primaryRulebookData,
    selectedSpaceId,
    setDeleteRulebook,
    isGroupTab,
    onRetryRulebookIndexing,
    retryRulebookIndexingInProgress,
    rulebookIndexingStatus,
  } = props;

  const { logo_url } =
    genralRulebook == GENERAL_GPT_EDIT
      ? selectedSpaceId || {}
      : isPrimaryGptEditOpen
      ? selectedSpaceId || {}
      : genralRulebook != GENERAL_GPT && specificRulebookData?.[0]
      ? specificRulebookData?.[0] || {}
      : {};

  const uploadedRulebooks = useMemo(() => {
    if (genralRulebook === GENERAL_GPT_EDIT) {
      return generalRulebookData?.filter(e => e.group_id === selectedSpaceId?.group_id) || [];
    }
    if (isPrimaryGptEditOpen) {
      return primaryRulebookData?.filter(e => e.group_id === selectedSpaceId?.group_id) || [];
    }
    if (genralRulebook !== GENERAL_GPT && specificRulebookData?.length) {
      return specificRulebookData;
    }
    return [];
  }, [
    generalRulebookData,
    genralRulebook,
    isPrimaryGptEditOpen,
    primaryRulebookData,
    selectedSpaceId?.group_id,
    specificRulebookData,
  ]);

  const uploadedRulebooksKey = uploadedRulebooks
    .map(file => `${file.file_id || file.file_url || file.id}:${file.file_name || ''}`)
    .join('|');
  const [uploadedRulebooksArray, setUploadedRulebooksArray] = useState(uploadedRulebooks);

  useEffect(() => {
    setUploadedRulebooksArray(uploadedRulebooks);
  }, [uploadedRulebooksKey]);

  useEffect(() => {
    if (!uploadInProgress) {
      setUploadProgressPercent(0);
      return undefined;
    }

    setUploadProgressPercent(5);
    return undefined;
  }, [uploadInProgress]);

  useEffect(() => {
    let isMounted = true;

    import('react-pdf').then(({ Document, Page, pdfjs }) => {
      pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

      if (isMounted) {
        setPdfPreview(() => ({ Document, Page }));
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const submitDisabled = uploadInProgress || !values?.rulebookName
    || (values?.documentFiles ? values?.documentFiles?.length : uploadedRulebooksArray.length) == 0 || !(values?.rulebookLogo || (!isLogoRemoved && logo_url));

  //prevents from flickering issue
  const documentFileURL = values?.documentFiles || [];

  //   const handleDeleteRulebookFile = (file_id) => {
  //     handleDeleteRulebook(file_id); // Use updated state immediately

  //     // setDeletedFileIds((prev) => {
  //     //     const updatedFileIds = [...prev, file_id]; // Ensure correct array format
  //     //     handleDeleteRulebook(updatedFileIds); // Use updated state immediately
  //     //     return updatedFileIds;
  //     // });
  // };

  const handleDeleteFile = file_id => {
    setUploadedFiles(prev => {
      const updatedFiles = prev.filter(file => file.file_id !== file_id); // Remove selected file
      form.change('documentFiles', updatedFiles); // Update form state
      form.change('documentFile', null); // Reset file field in form

      // ✅ Reset file input field
      const fileInput = document.getElementById('documentFile');
      if (fileInput) {
        fileInput.value = ''; // Clear the selected file from input field
      }
      return updatedFiles; // Update state
    });
  };

  const removeImage = () => {
    form.batch(() => {
      form.change('rulebookLogo', null);
      form.change('rulebookLogoId', null);
    });
    setIsLogoRemoved(true);
    const fileInput = document.getElementById('rulebookLogo');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const markPdfPreviewFailed = fileId => {
    setFailedPdfPreviews(prev => ({
      ...prev,
      [fileId]: true,
    }));
  };

  const renderPdfFallback = ({ fileName, fileUrl }) => {
    const content = (
      <>
        <span className={css.pdfFallbackBadge}>PDF</span>
        <span className={css.pdfFallbackText}>{fileUrl ? 'View PDF' : 'Preview unavailable'}</span>
      </>
    );

    return fileUrl ? (
      <a
        className={css.pdfFallback}
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {content}
      </a>
    ) : (
      <div className={css.pdfFallback} title={fileName}>
        {content}
      </div>
    );
  };

  const handleRetryRulebookIndexing = rulebook => {
    if (!onRetryRulebookIndexing || !rulebook?.file_id) {
      return;
    }

    onRetryRulebookIndexing({
      file_id: rulebook.file_id,
      group_id: rulebook.group_id,
      rulebook_id: rulebook.rulebook_id,
    });
  };

  const ACCEPT_IMAGES =
    typeof window != 'undefined' &&
    window?.navigator?.platform &&
    window.navigator.platform.toLowerCase().includes('win')
      ? '.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.tiff'
      : 'image/*';

  return (
    <Form onSubmit={values => handleSubmit(values, form)} className={className}>
      <div className={css.fileInputWrapper}>
        <FieldTextInput
          className={css.inputBox}
          name="rulebookName"
          id="rulebookName"
          label="Rulebook Name"
          placeholder="Rulebook Name"
          maxLength={50}
          required
        />
        <div className={css.uploadGrid}>
          {/* logo upload */}
          <Field
            accept={ACCEPT_IMAGES}
            id="rulebookLogo"
            name="rulebookLogo"
            // label={'Add Rulebook logo here'}
            type="file"
            form={null}
            uploadImageError={uploadError}
            disabled={false}
          >
            {fieldProps => {
              const { accept, id, input, label, disabled } = fieldProps;

              const { name, type } = input;
              const onChange = async e => {
                let file = e.target.files[0];
                if (file) {
                  {
                    /* if file type is heic */
                  }
                  if (file.type.toLowerCase() == 'image/heic') {
                    try {
                      const blob = await heic2any({
                        blob: file,
                        toType: 'image/jpeg',
                        quality: 1, // Adjust quality (0 to 1)
                      });

                      file = new File([blob], file.name.replace('.heic', '.jpg'), {
                        type: 'image/jpeg',
                      });
                    } catch (error) {
                      console.error('HEIC conversion failed:', error);
                      alert('This browser may not support HEIC conversion.');
                      return file; // Return original file in case of failure
                    }
                  }

                  const uuid = crypto.randomUUID();
                  form.batch(() => {
                    form.change(`rulebookLogo`, file);
                    form.change('rulebookLogoId', uuid);
                  });
                  setIsLogoRemoved(false);
                }
              };
              return (
                <div className={css.uploadAvatarWrapper}>
                  <label className={css.label} htmlFor={id}>
                    {label}
                  </label>
                  <div className={css.uploadAvatarInputWrapper}>
                    <div>
                      {values?.rulebookLogo || (!isLogoRemoved && logo_url) ? (
                        <div className={css.closeIcon} onClick={() => removeImage()}>
                          <IconProfileCard type="close" />
                        </div>
                      ) : null}

                      {values?.rulebookLogo || (!isLogoRemoved && logo_url) ? (
                        <img
                          src={
                            values?.rulebookLogo
                              ? URL.createObjectURL(values?.rulebookLogo)
                              : logo_url
                          }
                          alt="Preview"
                          width={'100px'}
                        />
                      ) : (
                        <>
                          <IconCollection name="uploadIcon" />
                          <div>
                            <p>
                              <FormattedMessage id="RequestRulebookForm.rulebookLogo" />
                            </p>
                          <p className={css.pdfText}></p>
                          </div>
                        </>
                      )}
                      {values?.rulebookLogo || (!isLogoRemoved && logo_url) ? null : (
                        <Button className={css.chooseFileButton}>
                          <FormattedMessage id="RequestRulebookForm.dropImageButton" />
                        </Button>
                      )}

                    </div>
                    <input
                      accept={accept}
                      id={id}
                      name={name}
                      className={css.uploadAvatarInput}
                      disabled={disabled}
                      onChange={onChange}
                      type={type}
                    />
                  </div>
                  {typeof uploadError == 'string' && uploadError ? (
                    <FormattedMessage id="RequestRulebookForm.failedToUpload" />
                  ) : null}
                </div>
              );
            }}
          </Field>
          {(uploadedRulebooksArray?.length || 0) + (documentFileURL?.length || 0) < 10 ? (
            <Field
              accept={ACCEPT_FILES}
              id="documentFile"
              name="documentFile"
              // label={'Add Rulebook'}
              type="file"
              form={null}
              uploadImageError={uploadError}
              disabled={false}
            >
              {fieldProps => {
                const { accept, id, input, label, disabled } = fieldProps;
                const { name, type } = input;
                const onChange = e => {
                  const newFiles = Array.from(e.target.files); // Convert FileList to an array

                  setUploadedFiles(prev => {
                    const totalExistingFiles = prev.length;
                    const remainingSlots = 10 - totalExistingFiles;

                    if (remainingSlots <= 0) {
                      alert('You can only upload up to 10 files.');
                      return prev; // Prevent adding more files
                    }

                    const filesToAdd = newFiles.slice(0, remainingSlots); // Only take the allowed number

                    const fileData = filesToAdd.map(file => ({
                      file,
                      file_id: crypto.randomUUID(),
                      documentFileView: URL.createObjectURL(file),
                    }));

                    const updatedFiles = [...prev, ...fileData];

                    form.batch(() => {
                      form.change('documentFiles', updatedFiles);
                    });
                    return updatedFiles; // Ensure state updates properly
                  });
                };

                return (
                  <div className={css.uploadAvatarWrapper}>
                    <label className={css.label} htmlFor={id}>
                      {label}
                    </label>
                    <div className={css.uploadAvatarInputWrapper}>
                      <div>
                        {
                          <>
                            <IconCollection name="uploadIcon" />
                            <div>
                              <p>
                                Add Rulebook Files
                                {/* <FormattedMessage id="AddDocumentModal.dropFileDescription" /> */}
                              </p>
                              <p className={css.pdfText}>
                                PDF, DOC, or DOCX file
                              </p>
                            </div>
                          </>
                        }
                        {
                          <Button className={css.chooseFileButton}>
                            <FormattedMessage id="AddDocumentModal.dropFileButton" />
                          </Button>
                        }
                      </div>
                      <input
                        accept={accept}
                        id={id}
                        name={name}
                        className={css.uploadAvatarInput}
                        disabled={disabled}
                        onChange={onChange}
                        type={type}
                        multiple
                      />
                    </div>
                    {typeof uploadError == 'string' && uploadError ? (
                      <FormattedMessage id="RequestRulebookForm.failedToUpload" />
                    ) : null}
                  </div>
                );
              }}
            </Field>
          ) : null}
        </div>
      </div>
      {documentFileURL?.length > 0 ? (
        <div className={css.uplaodedPdf}>
          {documentFileURL.map(url => {
            return (
              <div className={css.uploadedPdfCard} key={url.file_id}>
                <div className={css.previewFrame}>
                  <button
                    type="button"
                    className={css.removeFileButton}
                    onClick={() => {
                      handleDeleteFile(url.file_id);
                    }}
                  >
                    <IconProfileCard type="close" />
                  </button>
                  {PdfPreview && !failedPdfPreviews[url.file_id] ? (
                    <PdfPreview.Document
                      file={url.documentFileView}
                      loading={renderPdfFallback({ fileName: url.file.name })}
                      error={renderPdfFallback({ fileName: url.file.name })}
                      noData={renderPdfFallback({ fileName: url.file.name })}
                      onLoadError={() => markPdfPreviewFailed(url.file_id)}
                    >
                      <PdfPreview.Page
                        pageNumber={1}
                        width={96}
                        onLoadError={() => markPdfPreviewFailed(url.file_id)}
                      />
                    </PdfPreview.Document>
                  ) : (
                    renderPdfFallback({ fileName: url.file.name })
                  )}
                </div>
                <div className={css.nameWrapper}>
                  {url.file.name
                    .split('.')
                    .slice(0, -1)
                    .join('.')
                    ?.slice(0, 12) + '...'}
                </div>
                <RulebookProgress
                  uploadInProgress={uploadInProgress}
                  progressPercent={uploadProgressPercent}
                />
              </div>
            );
          })}
        </div>
      ) : null}
      {uploadedRulebooksArray?.length > 0 ? (
        <div className={css.uplaodedPdf}>
          {uploadedRulebooksArray
            .filter(file => file.file_name.toLowerCase().endsWith('.pdf'))
            .map((url, i) => {
              const statusFromApi = getStatusForRulebookFile({
                rulebook: url,
                statusResponse: rulebookIndexingStatus,
              });
              const uploadProcess = statusFromApi?.status || url.upload_process;
              const progressPercent = statusFromApi?.percent;

              return (
                <div className={css.uploadedPdfCard} key={url.file_url}>
                  <div className={css.previewFrame}>
                    <button
                      type="button"
                      className={css.removeFileButton}
                      onClick={() => {
                        setUploadedRulebooksArray(
                          uploadedRulebooksArray.filter((file, index) => index != i)
                        );
                        setDeleteRulebook(url);
                        handleDeleteRulebook(url);
                      }}
                    >
                      <IconProfileCard type="close" />
                    </button>
                    {renderPdfFallback({ fileName: url.file_name, fileUrl: url.file_url })}
                  </div>
                  <div className={css.nameWrapper}>
                    {url.file_name
                      .split('.')
                      .slice(0, -1)
                      .join('.')
                      ?.slice(0, 12) + '...'}
                  </div>
                  {uploadProcess ? (
                    <div className={css.indexingStatusRow}>
                      <span
                        className={`${css.statusBadge} ${getRulebookStatusClass(
                          uploadProcess
                        )}`}
                      >
                        {getRulebookDisplayStatus(uploadProcess)}
                      </span>
                      {uploadProcess === RULEBOOK_INDEXING_STATUS.FAILED ? (
                        <button
                          type="button"
                          className={css.retryButton}
                          disabled={retryRulebookIndexingInProgress}
                          onClick={() => handleRetryRulebookIndexing(url)}
                        >
                          {retryRulebookIndexingInProgress ? 'Retrying' : 'Retry'}
                        </button>
                      ) : null}
                      <RulebookProgress status={uploadProcess} progressPercent={progressPercent} />
                    </div>
                  ) : null}
                </div>
              );
            })}
        </div>
      ) : null}

      {uploadError ? (
        <div className={css.error}>
          <FormattedMessage
            id="UploadDocumentForm.uploadError"
            values={{ error: uploadError?.message }}
          />
        </div>
      ) : null}

      <Button
        className={css.submitButton}
        type="submit"
        inProgress={uploadInProgress}
        disabled={submitDisabled}
      >
        {/* <FormattedMessage id="UploadDocumentForm.upload" /> */}+ Add Rulebook
      </Button>
    </Form>
  );
};

UploadDocumentFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  uploadError: null,
  uploadInProgress: false,
};

UploadDocumentFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  handleSubmit: func.isRequired,
  uploadInProgress: bool,
  uploadError: string,
  form: object.isRequired,
  pristine: bool.isRequired,
  invalid: bool.isRequired,
  rulebookIndexingStatus: object,
};

const UploadDocumentForm = props => {
  const { intl } = props;

  return (
    <FinalForm
      {...props}
      validate={values => {
        // const errors = {};
        // if (!values.documentFile) {
        //   errors.documentFile = intl.formatMessage({ id: 'UploadDocumentForm.fileRequired' });
        // }
        // return errors;
      }}
      render={fieldRenderProps => <UploadDocumentFormComponent {...fieldRenderProps} />}
    />
  );
};

export default UploadDocumentForm;
